import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { CheckCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PunttThinking } from "../../../components/PunttThinking";

interface ThinkingStateProps {
  /** undefined = no assistant message yet (phase 0) */
  message?: AskPunttMessage;
  /**
   * True when the user's latest message had file attachments and the backend
   * is still in its pre-agent read phase. Fills the silent pause while
   * `_pre_read_attachments` runs before any agent step fires.
   */
  precedingUserHasAttachments?: boolean;
  /** True once the response has rendered; keeps a compact status summary. */
  completed?: boolean;
}

function getPhase(
  message?: AskPunttMessage,
  precedingUserHasAttachments?: boolean,
): {
  phase: number;
  key: string;
  params?: Record<string, unknown>;
} {
  const hasSteps = (message?.agentSteps?.length ?? 0) > 0;
  const hasSearching = message?.searchProgress?.some(
    (s) => s.status === "searching",
  );
  const hasStreamingContent = !!message?.streamingContent;

  if (hasStreamingContent) {
    return { phase: 4, key: "thinkingState.writingResponse" };
  }

  if (hasSteps) {
    return { phase: 3, key: "thinkingState.analyzing" };
  }

  if (hasSearching) {
    const searchingSource = message?.searchProgress?.find(
      (s) => s.status === "searching",
    );
    return {
      phase: 2,
      key: "thinkingState.researching",
      params: { source: searchingSource?.source ?? "" },
    };
  }

  if (precedingUserHasAttachments) {
    return { phase: 1, key: "thinkingState.readingAttachments" };
  }

  if (!message) {
    return { phase: 0, key: "thinkingState.thinking" };
  }

  return { phase: 1, key: "thinkingState.startingUp" };
}

export function ThinkingState({
  message,
  precedingUserHasAttachments,
  completed = false,
}: ThinkingStateProps) {
  const { t } = useTranslation("ui");
  const { key, params } = getPhase(message, precedingUserHasAttachments);
  const text = t(key, params).trim();
  const [displayText, setDisplayText] = useState(text);
  const [fading, setFading] = useState(false);
  const prevTextRef = useRef(text);
  const summaryText = completed
    ? t("thinkingState.startedResponse")
    : displayText;

  useEffect(() => {
    if (text !== prevTextRef.current) {
      setFading(true);
      const timer = setTimeout(() => {
        setDisplayText(text);
        setFading(false);
        prevTextRef.current = text;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [text]);

  return (
    <div className="my-3">
      <div
        data-testid="ask-puntt-thinking-state"
        role="status"
        aria-live="polite"
        className="flex items-center gap-2"
      >
        {completed ? (
          <CheckCircle
            size={16}
            weight="fill"
            className="shrink-0 text-malachite-600"
            aria-hidden
          />
        ) : (
          <PunttThinking size={16} label={null} className="shrink-0" />
        )}
        <Text
          size="1"
          weight="regular"
          className={`text-puntt-neutral-gray-12 transition-opacity duration-150 ${fading ? "opacity-0" : "opacity-100"}`}
        >
          {summaryText}
        </Text>
      </div>
    </div>
  );
}
